import {
  Collapse,
  TableCell,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Settings, InfoOutlined } from "@mui/icons-material";
import { UseFormReturn } from "react-hook-form";
import { ProjectFormType } from "../../hooks/useCreateProject";
import { DynamicField, SelectOption } from "../../models/form";
import { CapabilityTool } from "../../models/tools";
import HeiCheckbox from "../Form/HeiCheckbox/HeiCheckbox";
import HeiRadioGroup from "../Form/HeiRadioGroup/HeiRadioGroup";
import ToolCustomFields from "../ToolCustomFields/ToolCustomFields";
import ToolDetailsFieldsInvitationPopup from "../ToolDetailsFieldsInvitationPopup/ToolDetailsFieldsInvitationPopup";
import CustomGroups from "../../../Projects/ProjectDetails/components/CustomGroups/CustomGroups";
import AzureFeeds from "../../../Projects/ProjectDetails/components/Feeds/Feeds";
import Repositories from "../../../Projects/ProjectDetails/components/Repositories/Repositories";
import GitHubCopilotBudgetsModal from "../../../Projects/ProjectDetails/components/GitHubCopilotBudgetsModal/GitHubCopilotBudgetsModal";
import { ToolStatusEnum } from "../../api/models/ProjectTool";
import { useMemo, useState } from "react";
import HeiGroupButton from "../Form/HeiGroupButton/HeiGroupButton";
import enumToKeyValue from "../../utils/enumToSelectOptions";
import { ToolVisibility } from "../../models/toolVisibilityEnum";
import { InviteMode } from "../../models/inviteMode";
import styles from "./ToolDetailsFieldsTableRow.module.scss";

const inviteUserOptions = (isEdit = false) =>
  [
    {
      label: isEdit ? "Automatically" : "Automatically (upon creation)",
      value: "Automatically",
    },
    {
      label: isEdit ? "Manually" : "Manually (later)",
      value: "Manually",
    },
  ] as SelectOption[];

const dbbEnvironmentOptions: SelectOption[] = [
  { label: "Production", value: "Production" },
  { label: "Non-production", value: "NonProduction" },
];

interface ToolDetailsFieldsTableRowProps {
  toolOpen: boolean;
  index: number;
  form: UseFormReturn<ProjectFormType>;
  CustomFields?: DynamicField<ProjectFormType>[];
  toolKey: CapabilityTool;
  isEdit?: boolean;
  userCanEdit: boolean;
  canManageAzureResources?: boolean;
  canManageGithubRepositories?: boolean;
  status?: ToolStatusEnum;
  useCustomRoleMapping: boolean;
  projectId?: string;
  toolId?: string;
  toolProjectId?: string;
}

const ToolDetailsFieldsTableRow = ({
  form,
  toolOpen,
  index,
  toolKey,
  CustomFields,
  isEdit,
  userCanEdit,
  canManageAzureResources = false,
  canManageGithubRepositories = false,
  status,
  useCustomRoleMapping,
  projectId,
  toolId,
  toolProjectId,
}: ToolDetailsFieldsTableRowProps): JSX.Element => {
  const [isCustomGroupsModalOpen, setIsCustomGroupsModalOpen] = useState(false);
  const [isAzureFeedsModalOpen, setIsAzureFeedsModalOpen] = useState(false);
  const [isRepositoriesModalOpen, setIsRepositoriesModalOpen] = useState(false);
  const [isInviteModeConfirmOpen, setIsInviteModeConfirmOpen] = useState(false);
  const [isBudgetsModalOpen, setIsBudgetsModalOpen] = useState(false);

  const toolCustomFields = useMemo(() => {
    return isEdit
      ? CustomFields?.filter((c) => {
          if (c.onCreationOnly) return false;
          // Hide ServiceNow group and JiraWorkflowScheme for Portfolio/Strategy packages
          if (useCustomRoleMapping && c.label === "ServiceNow group") {
            return false;
          }
          return true;
        })
      : CustomFields?.filter((c) => {
          if (c.label === "ServiceNow group") return false;
          return true;
        });
  }, [isEdit, CustomFields, useCustomRoleMapping]);

  const JiraHideSection = () => (
    <>
      <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
        Hide project on Jira
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        By checking this box, this project will not be visible for other users
        on Jira
      </Typography>
      <HeiCheckbox
        control={form.control}
        disabled={!userCanEdit}
        name={`tools.${index}.isHidden`}
        label="Hide project"
        tooltip="Only allows users explicitly granted access to see your backlog. Your data will not be loaded into AEP datalake for reporting purposes. See FAQ for details. "
      />
    </>
  );

  const ConfluenceHideSection = () => (
    <>
      <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
        Set confluence visibility
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        By changing this option, this project will be visible for other
        Confluence users
      </Typography>
      <HeiGroupButton
        control={form.control}
        disabled={!userCanEdit}
        name={`tools.${index}.toolVisibility`}
        label=""
        options={enumToKeyValue(ToolVisibility)}
      />
    </>
  );

  const dbbEnvironment = form.watch(`tools.${index}.dbbEnvironment` as any) as
    | "Production"
    | "NonProduction"
    | undefined;
  const isDbbChecked = !!dbbEnvironment;

  const handleDbbCheckboxChange = (checked: boolean) => {
    if (checked) {
      form.setValue(`tools.${index}.dbbEnvironment` as any, "Production", {
        shouldDirty: true,
      });
    } else {
      form.setValue(`tools.${index}.dbbEnvironment` as any, undefined, {
        shouldDirty: true,
      });
    }
  };

  const NewRelicDbbSection = () => (
    <>
      <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
        DBB connection
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        By checking this box, this New Relic project will be connected to the
        DBB central account.
      </Typography>

      <Typography variant="body2" sx={{ mb: 1 }}>
        This can only be set during project creation and adding New Relic to an
        existing project.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={isDbbChecked}
            disabled={!userCanEdit}
            onChange={(e) => handleDbbCheckboxChange(e.target.checked)}
          />
        }
        label="Is part of DBB"
      />
      {isDbbChecked && (
        <>
          <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
            DBB environment
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Select which DBB environment this project belongs to.
          </Typography>
          <HeiGroupButton
            control={form.control}
            disabled={!userCanEdit}
            name={`tools.${index}.dbbEnvironment` as any}
            label=""
            options={dbbEnvironmentOptions}
          />
        </>
      )}
    </>
  );

  const handleInviteModeBeforeChange = (newValue: string) => {
    if (newValue === InviteMode.Automatically) {
      setIsInviteModeConfirmOpen(true);
      return false;
    }
    return true;
  };

  const InviteModeSection = () => (
    <>
      <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
        Invite users
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Select how you would like to invite users to this project.
      </Typography>

      <HeiRadioGroup
        disabled={toolKey == "BACKLOG:JIRACLOUD" || !userCanEdit}
        control={form.control}
        options={inviteUserOptions(isEdit)}
        name={`tools.${index}.inviteMode`}
        onBeforeChange={handleInviteModeBeforeChange}
      />
    </>
  );

  const ManageCustomGroupsButton = () => (
    <>
      <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
        Azure DevOps Permissions
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Manage Azure DevOps Custom Groups and Permissions
      </Typography>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Settings />}
          onClick={() => setIsCustomGroupsModalOpen(true)}
          disabled={!userCanEdit}
          sx={{ mt: 1, mb: 1 }}
        >
          Manage Custom Groups
        </Button>

        <Tooltip
          title={
            <span>
              Click here:{" "}
              <a
                href="https://toolchain.heineken.com/faq/d645b2ae-b48d-4724-9042-08de8fcf512e"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.tooltipLink}
              >
                Managing Azure Dev Ops custom groups functionality in Toolchain
                SSP
              </a>{" "}
              to learn more about Azure DevOps Custom Groups
            </span>
          }
          arrow
        >
          <InfoOutlined fontSize="small" color={"action"} />
        </Tooltip>
      </div>
    </>
  );

  const GitHubCopilotBudgetsButton = () => (
    <>
      <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
        GitHub Copilot Budgets
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Manage GitHub Copilot budgets for project members
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Settings />}
        onClick={() => setIsBudgetsModalOpen(true)}
        disabled={!userCanEdit}
        sx={{ mt: 1, mb: 1 }}
      >
        Budgets
      </Button>
    </>
  );

  const ManageAzureFeedsButton = () => (
    <>
      <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
        Azure Feeds
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Manage Azure DevOps Artifact Feeds
      </Typography>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Settings />}
          onClick={() => setIsAzureFeedsModalOpen(true)}
          disabled={!canManageAzureResources}
          sx={{ mt: 1, mb: 1 }}
        >
          Manage Azure Feeds
        </Button>

        <Tooltip
          title={
            <span>
              Click here:{" "}
              <a
                href="https://toolchain.heineken.com/faq/6540020b-b271-4925-4ac7-08debd567ff7"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.tooltipLink}
              >
                Managing Azure Dev Ops Artifacts feeds functionality in
                Toolchain SSP
              </a>{" "}
              to learn more about Azure DevOps Artifact Feeds
            </span>
          }
          arrow
        >
          <InfoOutlined fontSize="small" color={"action"} />
        </Tooltip>
      </div>
    </>
  );

  const ManageRepositoriesButton = () => (
    <>
      <Typography variant="body2" sx={{ color: "#202020", mb: 1, mt: 1 }}>
        GitHub Enterprise Repositories
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Manage GitHub Enterprise Repositories
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Settings />}
        onClick={() => setIsRepositoriesModalOpen(true)}
        disabled={!canManageGithubRepositories}
        sx={{ mt: 1, mb: 1 }}
      >
        Manage Repositories
      </Button>
    </>
  );

  const PreDefinedParamsSection = () => (
    <div style={{ padding: "8px" }}>
      <InviteModeSection />

      {(!isEdit || status == ToolStatusEnum.New) &&
        toolKey == "MONITORING:NEWRELIC" && <NewRelicDbbSection />}
      {isEdit &&
        status == ToolStatusEnum.Created &&
        toolKey == "BACKLOG:JIRACLOUD" && <JiraHideSection />}
      {isEdit &&
        status == ToolStatusEnum.Created &&
        toolKey == "WIKI:CONFLUENCE" && <ConfluenceHideSection />}
      {isEdit &&
        status == ToolStatusEnum.Created &&
        toolKey.includes("VSTS") && <ManageCustomGroupsButton />}
      {isEdit &&
        status == ToolStatusEnum.Created &&
        toolKey === "ARTIFACT:VSTS" && <ManageAzureFeedsButton />}
      {isEdit &&
        status == ToolStatusEnum.Created &&
        toolKey.includes("GITHUBCOPILOT") && <GitHubCopilotBudgetsButton />}
      {isEdit &&
        status == ToolStatusEnum.Created &&
        toolKey.includes("GITHUBENTERPRISE") && <ManageRepositoriesButton />}
    </div>
  );

  const CustomFieldsSection = () => (
    <>
      <Collapse in={toolOpen}>
        <ToolCustomFields
          userCanEdit={userCanEdit}
          toolIndex={index}
          fields={toolCustomFields}
          form={form}
          toolStatus={status}
        />
      </Collapse>
    </>
  );
  return (
    <>
      {toolCustomFields?.find((f) => f.type === "JiraWorkflowScheme") && (
        <>
          <TableRow sx={{ background: "#f5f5f5" }}>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
              <CustomFieldsSection />
            </TableCell>
          </TableRow>
          <TableRow sx={{ background: "#f5f5f5" }}>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
              <Collapse in={toolOpen}>
                <PreDefinedParamsSection />
              </Collapse>
            </TableCell>
          </TableRow>
        </>
      )}
      {!toolCustomFields?.find((f) => f.type === "JiraWorkflowScheme") && (
        <TableRow sx={{ background: "#f5f5f5" }}>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
            <CustomFieldsSection />
          </TableCell>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
            <Collapse in={toolOpen}>
              <PreDefinedParamsSection />
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      <Dialog
        open={isCustomGroupsModalOpen}
        onClose={() => setIsCustomGroupsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "80vh" },
        }}
      >
        <DialogTitle>Manage Azure DevOps Custom Groups</DialogTitle>
        <DialogContent>
          {projectId && toolId && toolProjectId ? (
            <CustomGroups
              projectId={projectId}
              toolId={toolId}
              toolProjectId={toolProjectId}
            />
          ) : (
            <Typography variant="body2" color="error">
              Missing required parameters: projectId, toolId, or toolProjectId
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCustomGroupsModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isAzureFeedsModalOpen}
        onClose={() => setIsAzureFeedsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "80vh" },
        }}
      >
        <DialogTitle>Manage Azure Feeds</DialogTitle>
        <DialogContent>
          {toolId && toolProjectId && projectId ? (
            <AzureFeeds
              projectId={projectId}
              toolId={toolId}
              toolProjectId={toolProjectId}
            />
          ) : (
            <Typography variant="body2" color="error">
              Missing required parameters: toolId or toolProjectId
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAzureFeedsModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRepositoriesModalOpen}
        onClose={() => setIsRepositoriesModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "80vh" },
        }}
      >
        <DialogTitle>Manage Repositories</DialogTitle>
        <DialogContent>
          {projectId && toolId && toolProjectId ? (
            <Repositories
              projectId={projectId}
              toolId={toolId}
              toolProjectId={toolProjectId}
            />
          ) : (
            <Typography variant="body2" color="error">
              Missing required parameters: projectId, toolId, or toolProjectId
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRepositoriesModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ToolDetailsFieldsInvitationPopup
        open={isInviteModeConfirmOpen}
        onClose={() => setIsInviteModeConfirmOpen(false)}
        onConfirm={() => {
          form.setValue(`tools.${index}.inviteMode` as any, "Automatically", {
            shouldDirty: true,
          });
          setIsInviteModeConfirmOpen(false);
        }}
      />

      {projectId && toolKey.includes("GITHUBCOPILOT") && (
        <GitHubCopilotBudgetsModal
          open={isBudgetsModalOpen}
          onClose={() => setIsBudgetsModalOpen(false)}
          projectId={projectId}
        />
      )}
    </>
  );
};

export default ToolDetailsFieldsTableRow;
